import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import {
  EduPlatformApiError,
  fetchPlatformSchools,
  type PlatformSchoolSummary,
} from "@/api/edu-platform-client";
import { platformSchoolDetail, routes } from "@/router/all_routes";
import { canReadPlatformSchools } from "@/navigation/platform-access";
import { Navigate } from "react-router-dom";

function lifecycleBadge(status: string): string {
  if (status === "ARCHIVED") return "bg-secondary";
  if (status === "PURGED") return "bg-dark";
  return "bg-success";
}

export function PlatformSchoolsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<PlatformSchoolSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (!canReadPlatformSchools(user)) {
    return <Navigate to={routes.accessDenied} replace />;
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPlatformSchools()
      .then((result) => {
        if (!cancelled) {
          setItems(result.items);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof EduPlatformApiError ? err.message : "Chargement impossible.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="suite-platform-page">
      <header className="suite-platform-page__header">
        <div>
          <h1 className="h4 mb-1">Écoles</h1>
          <p className="text-muted small mb-0">
            Établissements Edu provisionnés — setup, effectifs et facturation plateforme.
          </p>
        </div>
        <span className="badge text-bg-light border">{items.length} école(s)</span>
      </header>

      {loading ? <p className="text-muted">Chargement…</p> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="alert alert-info">
          Aucune école enregistrée. Provisionnez un tenant via PEP ou l&apos;API interne Edu.
        </div>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover align-middle suite-platform-table">
            <thead>
              <tr>
                <th>École</th>
                <th>Plan</th>
                <th>Élèves</th>
                <th>Setup</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((school) => (
                <tr key={school.tenantSlug}>
                  <td>
                    <strong>{school.organizationName ?? school.tenantSlug}</strong>
                    <div className="small text-muted">{school.tenantSlug}</div>
                  </td>
                  <td>
                    {school.platformPlanDisplayName ?? "—"}
                    {school.platformBillingStatus === "SUSPENDED" ? (
                      <span className="badge text-bg-danger ms-1">Suspendu</span>
                    ) : null}
                  </td>
                  <td>
                    {school.studentCount}
                    {school.maxStudents != null ? ` / ${school.maxStudents}` : ""}
                  </td>
                  <td>
                    {school.setupCompleted ? (
                      <span className="text-success">Complet</span>
                    ) : (
                      <span>Étape {school.setupCurrentStep}</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${lifecycleBadge(school.lifecycleStatus)}`}>
                      {school.lifecycleStatus}
                    </span>
                  </td>
                  <td className="text-end">
                    <Link
                      to={platformSchoolDetail(school.tenantSlug)}
                      className="btn btn-sm btn-outline-primary"
                    >
                      Détail
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
